import { PageSEO } from '@/components/SEO';

export default function Personvern() {
  return (
    <div>
      <PageSEO title="Personvern" path="/personvern" description="Personvernerklæring for Side Kraft. Informasjon om hvordan vi samler inn, bruker og beskytter dine personopplysninger i henhold til GDPR." />
      {/* Hero Section */}
      <section className="pt-16 md:pt-20 pb-16 md:pb-20 bg-muted" data-testid="personvern-hero">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="max-w-3xl">
            <p className="font-manrope text-xs font-medium tracking-[0.3em] uppercase text-accent mb-4">
              Juridisk
            </p>
            <h1 className="font-syne font-bold text-5xl md:text-6xl text-primary tracking-tight leading-[0.95]">
              Personvernerklæring
            </h1>
            <p className="font-manrope text-lg text-muted-foreground mt-8 leading-relaxed">
              Sist oppdatert: Januar 2026
            </p>
          </div>
        </div>
      </section>

      {/* Content */}
      <section className="py-24 md:py-32" data-testid="personvern-content">
        <div className="max-w-3xl mx-auto px-6 lg:px-12">
          <div className="prose prose-lg max-w-none">
            <div className="space-y-12">
              <div>
                <h2 className="font-syne font-bold text-2xl text-primary mb-4">
                  1. Innledning
                </h2>
                <p className="font-manrope text-muted-foreground leading-relaxed">
                  Side Kraft tar personvern på alvor. Denne personvernerklæringen forklarer hvordan vi samler inn, bruker og beskytter dine personopplysninger når du bruker våre tjenester eller besøker vår nettside.
                </p>
              </div>

              <div>
                <h2 className="font-syne font-bold text-2xl text-primary mb-4">
                  2. Behandlingsansvarlig
                </h2>
                <p className="font-manrope text-muted-foreground leading-relaxed">
                  Side Kraft er behandlingsansvarlig for personopplysningene vi samler inn. Du kan kontakte oss på:
                </p>
                <ul className="font-manrope text-muted-foreground mt-4 space-y-2">
                  <li>E-post: post@sidekraft.no</li>
                  <li>Telefon: +47 400 81 956</li>
                  <li>Adresse: Oslo, Norge</li>
                </ul>
              </div>

              <div>
                <h2 className="font-syne font-bold text-2xl text-primary mb-4">
                  3. Hvilke opplysninger vi samler inn
                </h2>
                <p className="font-manrope text-muted-foreground leading-relaxed mb-4">
                  Vi kan samle inn følgende typer personopplysninger:
                </p>
                <ul className="font-manrope text-muted-foreground space-y-2 list-disc list-inside">
                  <li>Kontaktinformasjon (navn, e-post, telefonnummer)</li>
                  <li>Bedriftsinformasjon (firmanavn, organisasjonsnummer)</li>
                  <li>Kommunikasjon med oss (e-poster, meldinger)</li>
                  <li>Tekniske data (IP-adresse, nettlesertype, besøksstatistikk)</li>
                </ul>
              </div>

              <div>
                <h2 className="font-syne font-bold text-2xl text-primary mb-4">
                  4. Formål med behandlingen
                </h2>
                <p className="font-manrope text-muted-foreground leading-relaxed mb-4">
                  Vi behandler personopplysninger for følgende formål:
                </p>
                <ul className="font-manrope text-muted-foreground space-y-2 list-disc list-inside">
                  <li>Levere og forbedre våre tjenester</li>
                  <li>Kommunisere med deg om våre tjenester</li>
                  <li>Sende relevante tilbud og informasjon (med ditt samtykke)</li>
                  <li>Overholde juridiske forpliktelser</li>
                  <li>Analysere og forbedre nettsiden vår</li>
                </ul>
              </div>

              <div>
                <h2 className="font-syne font-bold text-2xl text-primary mb-4">
                  5. Rettslig grunnlag
                </h2>
                <p className="font-manrope text-muted-foreground leading-relaxed">
                  Vi behandler personopplysninger basert på: samtykke, oppfyllelse av avtale, rettslig forpliktelse, eller berettiget interesse. For markedsføring krever vi alltid eksplisitt samtykke.
                </p>
              </div>

              <div>
                <h2 className="font-syne font-bold text-2xl text-primary mb-4">
                  6. Deling av opplysninger
                </h2>
                <p className="font-manrope text-muted-foreground leading-relaxed">
                  Vi deler ikke personopplysninger med tredjeparter, med mindre det er nødvendig for å levere våre tjenester (f.eks. tekniske leverandører) eller vi er pålagt ved lov. Alle våre underleverandører er bundet av databehandleravtaler.
                </p>
              </div>

              <div>
                <h2 className="font-syne font-bold text-2xl text-primary mb-4">
                  7. Lagring og sikkerhet
                </h2>
                <p className="font-manrope text-muted-foreground leading-relaxed">
                  Vi lagrer personopplysninger så lenge det er nødvendig for formålet de ble samlet inn for. Vi benytter tekniske og organisatoriske sikkerhetstiltak for å beskytte dine opplysninger mot uautorisert tilgang, tap eller misbruk.
                </p>
              </div>

              <div>
                <h2 className="font-syne font-bold text-2xl text-primary mb-4">
                  8. Dine rettigheter
                </h2>
                <p className="font-manrope text-muted-foreground leading-relaxed mb-4">
                  Du har følgende rettigheter:
                </p>
                <ul className="font-manrope text-muted-foreground space-y-2 list-disc list-inside">
                  <li>Rett til innsyn i egne personopplysninger</li>
                  <li>Rett til retting av uriktige opplysninger</li>
                  <li>Rett til sletting under visse omstendigheter</li>
                  <li>Rett til å begrense behandlingen</li>
                  <li>Rett til dataportabilitet</li>
                  <li>Rett til å protestere mot behandling</li>
                  <li>Rett til å trekke tilbake samtykke</li>
                </ul>
              </div>

              <div>
                <h2 className="font-syne font-bold text-2xl text-primary mb-4">
                  9. Informasjonskapsler (cookies)
                </h2>
                <p className="font-manrope text-muted-foreground leading-relaxed">
                  Vår nettside bruker informasjonskapsler for å forbedre brukeropplevelsen og analysere trafikk. Du kan administrere dine preferanser for informasjonskapsler i nettleseren din.
                </p>
              </div>

              <div>
                <h2 className="font-syne font-bold text-2xl text-primary mb-4">
                  10. Kontakt og klager
                </h2>
                <p className="font-manrope text-muted-foreground leading-relaxed">
                  Hvis du har spørsmål om vår behandling av personopplysninger eller ønsker å utøve dine rettigheter, kontakt oss på post@sidekraft.no. Du har også rett til å klage til Datatilsynet hvis du mener at vi behandler personopplysninger i strid med personvernregelverket.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
